import {Injectable} from '@angular/core';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {SHELL_PROTOCOL_OPERATIONS_NS} from '../shell-communication/operations/shell-protocol-namespace.const';
import {AccessControlService} from '../access-control-service.class';
import {HedgePreset, presetPayload} from './hedge-preset.model';

class PresetOperation {
    constructor(private readonly operation: string, fields: object) { Object.assign(this, fields); }
    getOperationName(): string { return SHELL_PROTOCOL_OPERATIONS_NS + '.' + this.operation; }
}
@Injectable({providedIn: 'root'})
export class HedgePresetClient {
    constructor(private readonly shell: ShellClientService, private readonly access: AccessControlService) {}
    get canUse(): boolean { return this.access.isSecureElementAvailable('8396ad1e-cd32-4ca7-9e80-0ea2283a8ab1'); }
    get canManage(): boolean { return this.access.isSecureElementAvailable('0c46495e-5c23-4ec4-ad80-9e9c350e7403'); }
    async list(underlying: string, executionSide?: string, apgSource?: string): Promise<HedgePreset[]> {
        const reply = await this.shell.processQuery<{schemaVersion: number; strategies: string[]; sizingModes: string[]; optionSides: string[]; presets: string[]; applicabilityError?: string; spreadSizeMatching?: string}>(
            new PresetOperation('GetHedgePresets', {underlying, executionSide, apgSource}));
        if (reply.applicabilityError) { throw new Error(reply.applicabilityError); }
        if (executionSide && reply.spreadSizeMatching !== 'exactTemplate') {
            throw new Error('Refresh the server to enable exact template spread-size matching.');
        }
        if (reply.schemaVersion !== 5 || !reply.strategies || ['butterfly', 'vertical'].some(s => reply.strategies.indexOf(s) < 0)
            || !reply.sizingModes || reply.sizingModes.indexOf('dynamicWidth') < 0
            || !reply.optionSides || ['calls', 'puts', 'both'].some(side => reply.optionSides.indexOf(side) < 0)) {
            throw new Error('Hedging Presets Are Unavailable on This Server.');
        }
        const rows: HedgePreset[] = (reply.presets || []).map(json => JSON.parse(json));
        if (rows.some(p => ['calls', 'puts', 'both'].indexOf(p.optionSide) < 0 || ['butterfly', 'vertical'].indexOf(p.strategy) < 0)) {
            throw new Error('The Preset Catalogue Contains an Unsupported Option Side or Strategy.');
        }
        return rows.filter(p => !underlying || p.underlying === underlying);
    }
    async save(preset: HedgePreset): Promise<void> {
        try {
            await this.shell.processCommand(new PresetOperation('SaveHedgePreset', {
                preset: JSON.stringify(presetPayload(preset)), expectedRevision: preset.revision
            }));
        } catch (error) {
            // WCF command failures arrive as generic HTTP errors. Confirm the actual conflict
            // against the current catalogue rather than displaying a transport error as a name error.
            const rows = await this.list(null).catch(() => null);
            if (rows && rows.some(p => p.id !== preset.id && p.name.trim().toUpperCase() === preset.name.trim().toUpperCase())) {
                throw new Error('A Preset With This Display Name Already Exists. Choose a Different Name.');
            }
            throw error;
        }
    }
    remove(preset: HedgePreset): Promise<void> {
        return this.shell.processCommand(new PresetOperation('DeleteHedgePreset', {id: preset.id, expectedRevision: preset.revision}));
    }
}
